import type { Layout } from '../../types';

export const TrackableEvent = {
  BannerClicked: 'Banner Clicked',
  BannerSwiped: 'Banner Swiped',
  DepositMoney: 'Deposit Money',
  WithdrawMoney: 'Withdraw Money',
  FilterGames: 'Filter Games',
  FullScreenGamePageToggled: 'Full Screen Game Page Toggled',
  GameInfo: 'Game Info',
  GameLaunchRequest: 'Game Launch Request',
  GameLaunch: 'Game Launch',
  Login: 'Login',
  MultiViewUpdated: 'Multi View Updated',
  PageVisited: 'Page Visited',
  ModalOpened: 'Modal Opened',
  ModalClosed: 'Modal Closed',
  Promotions: 'Promotions',
  VideoPlaybackClick: 'Video Playback Click',
  NavigateTo: 'Navigate To',
  SearchExecuted: 'Search Executed',
  SearchFocusGained: 'Search Focus Gained',
  SocialMedia: 'Social Media',
  // New games tracking, these names should be used as is
  AUTO_LOGGED_OUT: 'autoLoggedOutPopup',
  CAROUSEL_BANNER_CLICKS: 'CarouselClicks',
  // Cornucopia (IGM-4081) — GTM trigger names. Confirmed by Roberta Burman
  // (product tracking) on IGM-4081, 2026-09-29: the FTD names start with a
  // lower-case `f`, unlike the ticket's table; every other name is as written.
  // A wrong literal makes the trigger silently not fire.
  CORNUCOPIA_DEPOSIT_ATTEMPT: 'depositAttempt',
  CORNUCOPIA_DEPOSIT_COMPLETE: 'depositComplete',
  CORNUCOPIA_DEPOSIT_CANCELLED: 'depositCancelled',
  CORNUCOPIA_DEPOSIT_FAILURE: 'depositFailure',
  CORNUCOPIA_FIRST_DEPOSIT_ATTEMPT: 'firstDepositAttempt',
  CORNUCOPIA_FIRST_DEPOSIT_COMPLETE: 'firstDepositComplete',
  CORNUCOPIA_FIRST_DEPOSIT_CANCELLED: 'ftdCancelled',
  CORNUCOPIA_FIRST_DEPOSIT_FAILURE: 'firstDepositFailure',
  CORNUCOPIA_WITHDRAWAL_EVENTS: 'withdrawalEvents',
  DEPOSIT_CLICKS: 'DepositClicks',
  DEPOSIT_OFFERS: 'DepositOffers',
  EMPTY_PG_LD: 'EmptyPgLD',
  FAVICON: 'favIcon',
  FOOTER_NAV_CLICKS: 'footerNavClicks',
  FORCE_UPDATE: 'ForceUpdate',
  GAME_PLAY: 'gamePlay',
  GAMES_HISTORY: 'gamesHistory',
  GAMES_LIMITS: 'gamesLimits',
  GAMES_LIMITED_MODE: 'gamesLimitedMode',
  GAMIFICATION_GAMES: 'gamificationGames',
  GAMIFICATION_HUB: 'gamificationHub',
  INACTIVE_TAB: 'InactiveTab',
  LIMIT_ENROLL_LOBBY: 'limitEnrollLobby',
  LIMIT_HISTORY: 'limitHistoryPage',
  LIMITS_AND_CLOSURE_PAGE: 'limitsAndClosurePage',
  LOBBY_BANNERS: 'LobbyBanners',
  LOBBY_MODALS: 'LobbyModals',
  LOGIN_CLICKS: 'LoginClicks',
  LOGOUT_PAGE: 'LogoutPg',
  MAINTENANCE_PG: 'MaintenancePg',
  MENU_NAV: 'menuNav',
  MY_ACCOUNT_MENU: 'myAccountMenu',
  NOTIFICATIONS: 'notifications',
  PANIC_BUTTON: 'panicButton',
  PAYMENT_HISTORY: 'paymentHistory',
  PRODUCT_SWITCHER: 'productSwitcher',
  PROMO_STORIES: 'promoStories',
  PROVIDER_COOLDOWN: 'ProviderCooldown',
  REALITY_CHECK: 'RealityCheck',
  LONG_PLAY: '15_min_cooldown_games',
  NIGH_PLAY: '6am_cooldown_games',
  REGISTER_CLICKS: 'RegisterClicks',
  RG_MODAL: 'rg_modal',
  SEARCH: 'gamesSearch',
  SESS_LOGGED_OUT_PG: 'SessLoggedOutPg',
  SH_GEO_POPUP: 'SHGeoPopup',
  TRANSACTION_HISTORY: 'transactionHistory',
  WELCOME_MODAL: 'WelcomeModal',
  SOURCE_OF_WEALTH: 'sourceOfWealth',
  GAMES_LUGAS_STATE: 'games_lugas_state',
  PROMOTIONAL_LOGIN_MODAL: 'Promotional_login_modal',
} as const;

export type TrackableEvent = (typeof TrackableEvent)[keyof typeof TrackableEvent];

export const TrackableEventAction = {
  APPLY: 'apply',
  BETTING_LIMIT_CTA: 'bettingLimitCTA',
  CHANGE_LIMIT_ATTEMPT: 'changeLimitAttempt',
  CLICK: 'click',
  CLOSE: 'close',
  CLOSURE_CTA: 'closureCTA',
  DEPOSIT: 'deposit',
  // Cornucopia (IGM-4081) — `withdrawalAction` values, as in the ticket
  // (confirmed 2026-09-29). There is deliberately no completed action: the
  // bank-side completion time is unknown, so it is not tracked.
  CORNUCOPIA_WITHDRAW_ATTEMPT: 'WithdrawAttempt',
  CORNUCOPIA_WITHDRAW_PENDING: 'WithdrawPending',
  CORNUCOPIA_WITHDRAW_FAILED: 'WithdrawFailed',
  DEPOSIT_LIMIT_CTA: 'depositLimitCTA',
  DEPOSIT_LIMIT_HISTORY_CTA: 'depositLimitHistoryCTA',
  DEPOSIT_LIMIT_HISTORY_PAGE_SHOWN: 'depositLimitHistoryPageShown',
  EDIT_LIMIT_FIELD_CLICK: 'editLimitFieldClick',
  FAVICON_DESELECTED: 'favIconDeselected',
  FAVICON_SELECTED: 'favIconSelected',
  FILTER_CTA: 'FilterCTA',
  FILTER_DATE_ALL: 'filterDateAll',
  FILTER_DATE_APPLY: 'filterDateApply',
  FIRST_SPIN_SUCCESS: 'firstSpinSuccess',
  FIRST_ROUND_COMPLETE: 'first_round_complete',
  GAME_ENDED: 'gameEnded',
  GAME_ENDED_SH: 'game_closed',
  GAME_INFO: 'gameInfo',
  GAME_OPEN_FAIL: 'gameOpenFail',
  GAME_OPEN_SUCCESS: 'gameOpenSuccess',
  GAME_SELECTED: 'gameSelected',
  GAME_SELECTED_INFO_PAGE: 'gameSelectedInfoPage',
  GAME_SELECTED_INFO_PAGE_IMG: 'gameSelectedInfoPageImg',
  GAME_SELECTED_INFO_PAGE_VID: 'gameSelectedInfoPageVid',
  GAME_UNAVAILABLE: 'gameUnavailablePageShown',
  GAME_UNAVAILABLE_MODAL_SHOWN: 'gameUnavailableModalShown',
  GAMES_APP: 'gamesApp',
  GAMIFICATION_ICON: 'gamificationIcon',
  HOME: 'home',
  IN_GAME_ERROR: 'inGameError',
  LIMIT_DURATION_CTA_CLICK: 'limitDurationCTAclick',
  LIMIT_DURATION_SCREEN_SHOWN: 'limitDurationScreenShown',
  LIMITS_CTA: 'limitsCTA',
  LIMITS_HISTORY_CTA: 'limitsHistoryCTA',
  LOBBY: 'lobby',
  LOGIN: 'login',
  LOSS_LIMIT_CTA: 'lossLimitCTA',
  LOSS_LIMIT_HISTORY_PAGE_SHOWN: 'lossLimitHistoryPageShown',
  NAVIGATE_TO: 'navigateTo',
  OK: 'OK',
  OPEN: 'open',
  REGISTER: 'register',
  REMOVE_LIMIT_ATTEMPT: 'removeLimitAttempt',
  RESET: 'reset',
  SEARCH_ATTEMPT: 'searchAttempt',
  SET_NEW_LIMIT_ATTEMPT: 'setNewLimitAttempt',
  SHOWN: 'shown',
  SPORTS: 'sports',
  SPORTS_APP: 'sportsApp',
  WAGERING_LOSS_LIMIT_HISTORY_CTA: 'wageringLossLimitHistoryCTA',
} as const;

export type TrackableEventAction = (typeof TrackableEventAction)[keyof typeof TrackableEventAction];

export const TrackingEventSource = {
  A11Y: 'accessibilityPage',
  ABOUT_US: 'about-us',
  ACCOUNT_MENU: 'accMenu',
  BOTTOM_NAV_DEPOSIT: 'bottomNavDeposit',
  CAROUSEL: 'carousel',
  CLUB1000X: 'club-1000x',
  CLUB1000X_LOBBY_PREVIEW: 'club-1000x-lobby-preview',
  CONTACT: 'contact',
  COOLDOWN_MODAL: 'cooldownModal',
  LOW_BALANCE_FLYER: 'lowBalanceFlyer',
  DESK_LOGIN_SIDE_NAV_CTA: 'DeskLoginSideNavCTA',
  FAQ: 'faq',
  FEATURED_GAME: 'featured-game',
  FS_WIDGET: 'fsWidget',
  GAME_DESCRIPTION_POPUP: 'game-description-popup',
  GAME_DISCOVERY_DRAWER: 'gameDiscoveryDrawer',
  GAME_INFO_PAGE: 'game-info-page',
  HEADER: 'header',
  HELP_CENTER: 'help-center',
  HOUSE_RULES: 'house-rules',
  IMPRINT: 'imprint',
  IOS_PAGE: 'ios-page',
  LOBBY: 'lobby',
  LOBBY_BANNER: 'lobby-banner',
  LOGIN_AND_PLAY: 'login-and-play',
  MISSIONS_BOTTOM_NAV_MODAL: 'MissionsBottomNavModal',
  MISSIONS_DESKTOP: 'missionsDesktop',
  MOBILE_BOTTOM_NAV: 'bottomNav',
  MY_PROMO: 'myPromo',
  MY_PROMO_MODAL: 'myPromoModal',
  MY_PROMO_PAGE: 'myPromoPage',
  MY_REWARDS: 'myRewards',
  NAV_SIDEBAR: 'nav-sidebar',
  NOTIFICATIONS: 'notifications',
  PRIVACY: 'privacy',
  PROVIDERS: 'providers',
  REGISTRATION_BANNER: 'registrationBanner',
  RESPONSIBLE_GAMING: 'responsible-gaming',
  REWARD_CARD: 'rewardCard',
  RTP: 'rtp',
  SEARCH_PAGE: 'searchPage',
  SELF_EXCLUSION: 'self-exclusion',
  SITEMAP: 'sitemap-page',
  TAC_ACCEPT: 'tac-accept',
  TERMS_AND_CONDITIONS: 'terms-and-conditions',
  TERMS_OF_OFFER: 'terms-of-offer',
} as const;

export type TrackingEventSource = (typeof TrackingEventSource)[keyof typeof TrackingEventSource];

export const NavigationTrigger = {
  BANNER: 'Banner',
  CATEGORY: 'Category',
  GAME_STUDIO: 'Game Studio',
  NAVBAR: 'Navbar',
} as const;

export type NavigationTrigger = (typeof NavigationTrigger)[keyof typeof NavigationTrigger];

export interface SearchAttemptTrackingResult {
  eventAction: 'searchSuccess' | 'searchFail' | 'searchError';
  searchTerm: string;
  searchSource: TrackingEventSource;
  frequency?: number;
  errorMessage?: string;
}

export interface ProductSwitcherTrackingParams {
  event: 'productSwitcher' | 'xsellGamesSH';
  eventAction: 'casino' | 'logged_out_modal' | 'logged_in_modal' | string;
  action?: 'shown' | 'closed' | 'goToGames' | 'confirm' | 'yes_i_am';
  status?: 'loggedIn' | 'loggedOut';
}

export interface GameEventTrackingParams {
  eventAction: TrackableEventAction | string;
  errorMessage?: string;
  errorSource?: 'internal' | 'external';
  gameCategory?: string;
  gameFilters?: string;
  gameMission?: string;
  gameName?: string;
  gamePosition?: number;
  gamePromo?: string;
  gameProvider?: string;
  gameReward?: string;
  gameSource?: string;
  gameSpinType?: string;
  gameTournament?: string;
  gameType?: string;
  select?: 'image' | 'gameInfoPG' | 'video' | string;
  spinCount?: string;
  stakeAmount?: string;
  winType?: GameEventTrackingWinType;
}

export type GameEventTrackingWinType = 'normal' | 'mega' | 'big';

export interface HistoryTrackingParams {
  eventAction: TrackableEventAction;
  errorMessage?: string;
  filter_date?: string;
}

export interface DepositOffersTrackingParams {
  event?: string;
  eventAction: TrackableEventAction | string;
  bonusCount?: number;
  BonusID?: string;
  bonusStatus?: string;
  bonusTypesAvail?: string;
  bonusType?: string;
  source?: 'optinPg' | 'optinInfoPg';
}

/** Cornucopia `bonusType` values (IGM-4081, confirmed 2026-09-29). */
export type CornucopiaBonusType = 'ftdBonus' | 'campaignBonus' | 'not-applicable';

/**
 * `deposit/params` (Cornucopia) → `dataLayer` tracking, IGM-4081.
 *
 * Built by `buildDepositEvent` (`service/cornucopia/cornucopia-tracking.ts`);
 * field sources in `docs/integrations/cornucopia.md` → "CX Tracking
 * (IGM-4081)". `event` is picked from `DepositParams.stage`
 * (`attempt|success|cancellation|failure` → `depositAttempt|depositComplete|
 * depositCancelled|depositFailure`, or the FTD variants
 * `firstDepositAttempt|firstDepositComplete|ftdCancelled|firstDepositFailure`).
 */
export interface CornucopiaDepositTrackingParams {
  event: string;
  method?: string;
  /** Attempt: Mastercard / Visa only (as Sports); every other stage: always. Omitted when absent. */
  processor?: string;
  /** Failure only: the message the user sees. */
  error?: string;
  /** Failure only: the backend error code. */
  errorCode?: string;
  depositAmount?: string;
  displayAmount?: 'yes' | 'no';
  bonus?: string;
  /** `ftdBonus` (welcome bonus), `campaignBonus`, or `not-applicable` when no bonus was offered. */
  bonusType?: CornucopiaBonusType;
  /** `TGAL` (Games) or `TKAL` (Casino). */
  entity: string;
  product: string;
  transactionID?: string;
  attemptID?: string;
  /** No real value exists (Sports will not adopt `cvfes/v2/status`); sent as the
   * placeholder `'not-available'` until it can be populated (Roberta, IGM-4081). */
  verificationStatus?: string;
}

/**
 * `withdrawal/params` (Cornucopia) → `dataLayer` tracking, IGM-4081.
 * `event` is always the static `'withdrawalEvents'`; `withdrawalAction`
 * carries the variant (`WithdrawAttempt|WithdrawPending|WithdrawFailed`),
 * picked by `buildWithdrawalEvent` from `WithdrawalParams.stage`.
 */
export interface CornucopiaWithdrawalTrackingParams {
  withdrawalAction: string;
  withdrawalMethod?: string;
  withdrawalAmount?: string;
  /** Failure only: the backend error code. */
  withdrawalError?: string;
  /** Failure only: the message the user sees. */
  withdrawalErrorMessage?: string;
  clientType?: string;
  origin: string;
  entity: string;
  product: string;
  transactionID?: string;
  attemptID?: string;
}

export interface GamesLimitsTrackingParams {
  eventAction: TrackableEventAction | string;
  limit_type?: 'bettingLimitGames' | 'lossLimitGames';
  limit_duration?: string;
  limit_duration_states?: string;
  limit_amount?: number; // Should be in EUR format `100.00`
  limitIncOrDec?: 'Increased' | 'Decreased' | 'Remove limit';
  currentLimit?: number;
  errorMessage?: string;
}

export interface CarouselBannerTrackingParams {
  eventAction: TrackableEventAction | string;
  promoName: string;
  promoPosition: number;
  url?: string;
}

export interface MenuNavTrackingParams {
  eventAction: TrackableEventAction | string;
  navMode: 'normalMode' | 'gameMode';
}

export interface SidebarMenuNavTrackingParams {
  eventAction: TrackableEventAction | string;
  navMode: 'normalMode' | 'gameMode';
}

export interface LobbyModalsTrackingParams {
  banner: string;
  errorMessage?: string;
  eventAction: TrackableEventAction | string;
}

export interface LimitedModeTrackingParams {
  eventAction: 'shown' | 'click';
  userState: string;
}

export interface LobbyBannersTrackingParams {
  banner: string;
  eventAction: TrackableEventAction | string;
}

export type SowBannerEventAction = 'Shown' | 'Start' | 'Close' | 'SendDocument';

export type SowWidget =
  | 'SOWNotificationBanner'
  | 'RGNotificationBanner'
  | 'AccountStatusWidget'
  | 'AccountStatusWidgetSOWBlock'
  | 'AccountStatusWidgetRGBlock';

export interface SourceOfWealthTrackingParams {
  eventAction: SowBannerEventAction;
  sowType: string;
  userState: string;
  widget: SowWidget;
  questionnaireAnswer?: string;
}

export interface RgProtectionModalTrackingParams {
  eventAction: 'shown' | 'click' | 'error';
  repeatDisplay?: 'on' | 'off';
  errorMessage?: string;
}

export interface LobbyStoriesTrackingParams {
  eventAction: LobbyStoriesEventAction;
  storyID?: string;
  campaignID?: string;
  assetType?: 'image' | 'video' | 'both';
  sound?: 'on' | 'off' | 'image';
  [key: string]: any;
}

export type WOFActionTypes =
  | 'WOF_accept_reward'
  | 'WOF_available_modal_shown'
  | 'WOF_available'
  | 'WOF_claim_reward'
  | 'WOF_close_game'
  | 'WOF_closed'
  | 'WOF_info_clicked'
  | 'WOF_info_instructions'
  | 'WOF_info_rewards'
  | 'WOF_onboarding_completed'
  | 'WOF_onboarding_started'
  | 'WOF_opened'
  | 'WOF_refuse_reward'
  | 'WOF_reject_reward'
  | 'WOF_reward_claimed'
  | 'WOF_skip_onboarding'
  | 'WOF_spun'
  | 'WOF_terms';

export interface GamificationGamesParams {
  active?: string;
  errors?: string;
  eventAction: string;
  fsAvail?: string;
  gameType?: string;
  missionID?: string;
  missionName?: string;
  missions?: string;
  missionsAvailable?: string;
  missionsStatus?: string;
  missionsType?: string;
  rewardID?: string;
  rewardName?: string;
  rewards?: string;
  rewardsAmount?: string;
  rewardsAvailable?: string;
  rewardsStatus?: string;
  rewardsType?: string;
  source?: string;
  stakeAmount?: string;
  status?: string;
  step?: string;
  tournamentID?: string;
  tournamentName?: string;
  tournaments?: string;
  tournamentsAvailable?: string;
  tournamentStatus?: string;
  tournamentType?: string;
  WOF_action?: WOFActionTypes;
}

export const LobbyStoriesEventAction = {
  STORIES_AVAILABLE: 'stories_available_on_lobby',
  STORY_SEEN: 'story_seen',
  HIGHLIGHTED: 'highlighted_story',
  LINK_CLICKED: 'story_link_clicked',
  TOTAL_SEEN: 'total_stories_seen',
  STORY_REPEAT: 'story_seen_more_than_once',
  ENGAGEMENT: 'engagement',
} as const;

export type LobbyStoriesEventAction = (typeof LobbyStoriesEventAction)[keyof typeof LobbyStoriesEventAction];

export interface SearchFollowUpTrackingParams {
  eventAction: 'followUpCategory' | 'followUpProvider' | 'followUpFrequentlySearched' | 'followUpResult';
  searchCategory?: string;
  searchProvider?: string;
}

export type LimitEnrollLobbyTrackingParam = 'shown' | 'setIndividual' | 'keepCurrent';
export type ProviderCooldownTrackingParam =
  | 'PopupShown'
  | 'cooldownStart'
  | 'cooldownMoreInfo'
  | 'cooldownClose'
  | 'cooldownBack'
  | 'cooldownInfoIcon';

export type LugasStateEventAction =
  | 'successfully_activated'
  | 'user_inactive'
  | 'user_deactivated_no_activity_seen'
  | 'user_deactivated_no_activity_ok'
  | 'user_active_with_another_provider_seen'
  | 'user_active_with_another_provider_ok'
  | 'oasis_suspended_result_seen'
  | 'oasis_suspended_result_ok'
  | 'oasis_error_seen'
  | 'oasis_error_ok'
  | 'generic_error_seen'
  | 'generic_error_retry'
  | 'generic_error_close';

export interface TrackingService {
  bannerClicked(device: Layout | undefined, properties: { [key: string]: any }): void;
  bannerSwiped(
    device: Layout | undefined,
    swipeDirection: 'Next' | 'Previous',
    properties: { [key: string]: any },
  ): void;
  depositMoney(source: TrackingEventSource): void;
  filterGames(title: string): void;
  fullScreenGamePageToggled(isFullScreen: boolean): void;
  gameInfo(gameId: string): void;
  gameLaunch(gameId: string, forReal: boolean): void;
  gameLaunchRequest(
    gameId: string,
    forReal: boolean,
    gameName: string | null,
    source: TrackingEventSource | string,
  ): void;
  login(source: TrackingEventSource): void;
  multiViewUpdated(numberOfViews: number): void;
  navigateTo(target: NavigationTrigger, name: string, viewAll?: boolean): void;
  pageVisited(path: string): void;
  modalClosed(modalId: string, userCancelled: boolean): void;
  modalOpened(modalId: string, source?: TrackingEventSource): void;
  promotions(source: TrackingEventSource): void;
  search(query: string): void;
  searchExecuted(searchValue: string, path: string): void;
  searchFocused(path: string): void;
  socialMedia(socialMediaType: string): void;
  videoPlaybackClick(gameId: string): void;
  withdrawMoney(source: TrackingEventSource | string): void;

  // Games Tracking
  accountMenuNav(menuName: string): void;
  autoLoggedOutPopup(eventAction: TrackableEventAction): void;
  carouselBanner(params: CarouselBannerTrackingParams): void;
  cornucopiaDeposit(params: CornucopiaDepositTrackingParams): void;
  cornucopiaWithdrawal(params: CornucopiaWithdrawalTrackingParams): void;
  customEvent(params: object): void;
  depositCTA(source: TrackingEventSource): void;
  depositOffers(params: DepositOffersTrackingParams): void;
  footerNav(link: string): void;
  forceUpdate(eventAction: 'shown' | 'update'): void;
  gameEnded(params: GameEventTrackingParams): void;
  gameOpenFailed(gameID: string, errorMessage: string): void;
  gamePlay(params: GameEventTrackingParams): void;
  gamesHistory(params: HistoryTrackingParams): void;
  gamesLimits(params: GamesLimitsTrackingParams): void;
  gamificationGames(params: GamificationGamesParams): void;
  gamificationHub(eventAction: 'shown' | 'close', errorMessage: string): void;
  inactiveTab(eventAction: 'shown' | 'useHere'): void;
  limitEnrollLobby(eventAction: LimitEnrollLobbyTrackingParam): void;
  limitHistory(eventAction: TrackableEventAction): void;
  limitsAndClosurePage(eventAction: TrackableEventAction): void;
  limitedMode(params: LimitedModeTrackingParams): void;
  lobbyBanners(params: LobbyBannersTrackingParams): void;
  lobbyModals(params: LobbyModalsTrackingParams): void;
  lobbyStories(params: LobbyStoriesTrackingParams): void;
  loginCTA(source: TrackingEventSource | string): void;
  logoutPage(event: TrackableEvent, eventAction: TrackableEventAction): void;
  maintenancePG(eventAction: 'shown'): void;
  menuNavigation(params: MenuNavTrackingParams): void;
  notifications(eventAction: 'shown' | 'click'): void;
  pageNotFound(eventAction: 'shown' | 'lobby', source?: string): void;
  panicButton(sourceURL: string): void;
  paymentHistory(params: HistoryTrackingParams): void;
  productSwitcher(params: ProductSwitcherTrackingParams): void;
  providerCooldown(eventAction: ProviderCooldownTrackingParam): void;
  realityCheck(eventAction: 'shown' | 'moreInfo' | 'home' | 'back' | 'continuePlaying' | 'info'): void;
  longPlay(
    eventAction: 'cooldown_popup_shown' | 'close' | 'click_to_play' | 'break_popup_shown' | 'break_popup_ok',
  ): void;
  nightPlay(
    eventAction:
      | 'cooldown_popup_shown'
      | 'cooldown_popup_close'
      | 'break_popup_shown'
      | 'break_popup_ok'
      | 'break_over_popup_shown',
  ): void;
  registerCTA(source: TrackingEventSource): void;
  searchAttempt(source: TrackingEventSource, query?: string): void;
  searchFollowUp(params: SearchFollowUpTrackingParams): void;
  searchResult(result: SearchAttemptTrackingResult): void;
  shGeoPopup(eventAction: 'shown' | 'go_to_games' | 'stay_on_sh'): void;
  toggleFavoriteGame(params: GameEventTrackingParams): void;
  transactionHistory(eventAction: TrackableEventAction): void;
  welcomeModal(eventAction: 'shown' | 'play'): void;
  sourceOfWealth(params: SourceOfWealthTrackingParams): void;
  lugasState(eventAction: LugasStateEventAction): void;
  promotionalLoginModal(eventAction: 'seen' | 'play now', entity: 'games' | 'sh', sound: 'on' | 'off'): void;
  rgProtectionModal(params: RgProtectionModalTrackingParams): void;
  /**
   * `payments-eligibility/tracking` — forwarded verbatim.
   *
   * This MFE's detail is already a complete dataLayer object, exactly as the
   * orchestrator treats it (`callGtm(event.detail)`). Remapping it onto the typed
   * methods above would drop fields the MFE owns (`restrictionCode`,
   * `restrictionType`, `entryPoint`) and would require inventing ones it does not
   * send (`widget`).
   */
  cornucopiaEligibility(payload: Record<string, unknown>): void;
}

export function getGameType(
  gameName: string,
): 'sic bo' | 'baccarat' | 'frenchRoulette' | 'americanRoulette' | 'craps' | 'blackjack' | 'poker' | '-' {
  const name = gameName.toLowerCase();

  if (name.includes('sicbo') || name.includes('sic bo')) {
    return 'sic bo';
  }
  if (name.includes('baccarat')) {
    return 'baccarat';
  }
  if (name.includes('french roulette')) {
    return 'frenchRoulette';
  }
  if (name.includes('american roulette')) {
    return 'americanRoulette';
  }
  if (name.includes('craps')) {
    return 'craps';
  }
  if (name.includes('blackjack')) {
    return 'blackjack';
  }
  if (name.includes('poker')) {
    return 'poker';
  }
  return '-';
}
